import * as React from "react";
import type firebase from "firebase/compat/app";
import {
    DateFormats,
    EmailRecipientType,
    IClient,
    INewOutboundEmail,
    OutboundEmailStatus,
    OutboundEmailType,
    OutboundEmailTypeDescription,
    TemplateResponse,
} from "intake_shared/src/types";
import { useToasts } from "react-toast-notifications";
import moment from "../../../../../utils/app_moment";
import Table, { TablePagination } from "../../../bits/Table/Table";
import store from "../../../../../store";
import { httpsCallable } from "firebase/functions";
import { fxs } from "../../../../../utils/firestore";
import TemplateList from "../TemplateList/TemplateList";
import { Dropdown } from "../../../bits/Dropdown/Dropdown";
import FilterButton from "../../../bits/FilterButton/FilterButton";
import { removeEnumUnderscore } from "../../../../helpers";
import styles from "./OutboundEmails.module.scss";
import { Button } from "../../../bits/Button/Button";
import RefreshIcon from "../../../icons/Refresh";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import EmailStatusTag from "../../../../../common/EmailStatusTag";
import { retryOutboundEmail } from "../../../../../store/email";
import Skeleton from "../../../bits/Skeleton/Skeleton";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";
import { SearchInput } from "../../../bits/SearchInput/SearchInput";

// Status descriptions for display
const OutboundEmailStatusDescription = {
    [OutboundEmailStatus.UNSCHEDULED]: "Unscheduled",
    [OutboundEmailStatus.PENDING]: "Pending",
    [OutboundEmailStatus.SENT]: "Sent",
    [OutboundEmailStatus.FAILED]: "Failed",
};

const EmailRecipientTypeDescription: Record<EmailRecipientType, string> = {
    patient: "Patient",
    clinician: "Clinician",
    internal: "Internal",
    office_manager: "Office Manager",
};

const PAGE_LIMIT = 50;
export const ALL_EMAIL_TYPES = "ALL";
export const ALL_EMAIL_STATUSES = "ALL";
export const ALL_RECIPIENT_TYPES = "ALL";

export type OutboundEmailTypeFilter = OutboundEmailType | typeof ALL_EMAIL_TYPES;
export type OutboundEmailStatusFilter =
    | OutboundEmailStatus
    | typeof ALL_EMAIL_STATUSES;
export type EmailRecipientTypeFilter =
    | EmailRecipientType
    | typeof ALL_RECIPIENT_TYPES;

interface OutboundEmailPaginationState {
    queryKey: string;
    currentPage: number;
    pageCursors: (firebase.firestore.QueryDocumentSnapshot | undefined)[];
}

export function getOutboundEmailTypes(
    filterByType: OutboundEmailTypeFilter
): OutboundEmailType[] {
    return filterByType === ALL_EMAIL_TYPES ? [] : [filterByType];
}

export function getOutboundEmailStatuses(
    filterByStatus: OutboundEmailStatusFilter
): OutboundEmailStatus[] {
    return filterByStatus === ALL_EMAIL_STATUSES
        ? Object.values(OutboundEmailStatus)
        : [filterByStatus];
}

interface OutboundEmailsProps {
    client_id?: string;
    client?: IClient;
    showRecipient?: boolean;
    statuses?: OutboundEmailStatus[];
    view?: "sent-emails" | "templates";
    onCountChange?: (count: number) => void;
}

export default function OutboundEmails({
    client_id = "",
    client,
    view = "sent-emails",
    onCountChange,
}: OutboundEmailsProps) {
    const { addToast } = useToasts();

    const [outboundEmails, setOutboundEmails] = React.useState(
        [] as INewOutboundEmail[]
    );
    const [templateResponse, setTemplatesResponse] = React.useState(
        {} as TemplateResponse
    );

    const [filterByStatus, setFilterByStatus] =
        React.useState<OutboundEmailStatusFilter>(ALL_EMAIL_STATUSES);
    const [filterByType, setFilterByType] =
        React.useState<OutboundEmailTypeFilter>(ALL_EMAIL_TYPES);
    const [filterByRecipientType, setFilterByRecipientType] =
        React.useState<EmailRecipientTypeFilter>(ALL_RECIPIENT_TYPES);
    const [searchQuery, setSearchQuery] = React.useState("");
    const queryKey = JSON.stringify([
        client_id,
        filterByStatus,
        filterByType,
        filterByRecipientType,
    ]);
    const [paginationState, setPaginationState] =
        React.useState<OutboundEmailPaginationState>({
            queryKey,
            currentPage: 1,
            pageCursors: [undefined],
        });
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const requestGeneration = React.useRef(0);
    const paginationMatchesQuery = paginationState.queryKey === queryKey;
    const currentPage = paginationMatchesQuery ? paginationState.currentPage : 1;
    const pageCursors = paginationMatchesQuery
        ? paginationState.pageCursors
        : [undefined];
    const currentPageCursor = pageCursors[currentPage - 1];
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const visibleOutboundEmails = normalizedSearchQuery
        ? outboundEmails.filter((email) =>
              [
                  removeEnumUnderscore(email.recipient_type),
                  email.recipient?.email,
                  email.recipient?.first_name,
                  email.subject,
                  email.preview_text,
                  email.status ? OutboundEmailStatusDescription[email.status] : "",
              ].some((value) => value?.toLowerCase().includes(normalizedSearchQuery))
          )
        : outboundEmails;

    const fetchSentOutboundEmails = React.useCallback(async () => {
        const activeRequestGeneration = ++requestGeneration.current;
        setLoading(true);
        try {
            const outboundEmailPage = await store.fetchOutboundEmails(
                client_id,
                getOutboundEmailStatuses(filterByStatus),
                getOutboundEmailTypes(filterByType),
                PAGE_LIMIT,
                currentPageCursor,
                filterByRecipientType === ALL_RECIPIENT_TYPES
                    ? undefined
                    : filterByRecipientType
            );

            if (activeRequestGeneration !== requestGeneration.current) {
                return;
            }

            setOutboundEmails(outboundEmailPage.records);
            setNextButtonDisabled(!outboundEmailPage.nextPageCursor);
            setPaginationState((previousPaginationState) => {
                const previousPageCursors =
                    previousPaginationState.queryKey === queryKey
                        ? previousPaginationState.pageCursors
                        : [undefined];
                const updatedPageCursors = previousPageCursors.slice(
                    0,
                    currentPage + 1
                );
                updatedPageCursors[currentPage] = outboundEmailPage.nextPageCursor;
                return {
                    queryKey,
                    currentPage,
                    pageCursors: updatedPageCursors,
                };
            });
        } finally {
            if (activeRequestGeneration === requestGeneration.current) {
                setLoading(false);
            }
        }
    }, [
        client_id,
        currentPage,
        currentPageCursor,
        filterByStatus,
        filterByType,
        filterByRecipientType,
        queryKey,
    ]);

    React.useEffect(() => {
        if (view !== "sent-emails") {
            return;
        }
        fetchSentOutboundEmails();
        return () => {
            requestGeneration.current += 1;
        };
    }, [fetchSentOutboundEmails, view]);

    const handleFirstPageClick = () => {
        setPaginationState({
            queryKey,
            currentPage: 1,
            pageCursors,
        });
    };

    const handlePrevClick = () => {
        if (currentPage > 1) {
            setPaginationState({
                queryKey,
                currentPage: currentPage - 1,
                pageCursors,
            });
        }
    };

    const handleNextClick = () => {
        setPaginationState({
            queryKey,
            currentPage: currentPage + 1,
            pageCursors,
        });
    };

    const getAllTemplates = React.useCallback(async () => {
        const getAllTemplatesFunction = httpsCallable(
            fxs,
            "httpFunctionExportGetEmailTemplates"
        );
        const result = await getAllTemplatesFunction();
        setTemplatesResponse(result.data as any);
    }, []);

    React.useEffect(() => {
        if (view !== "templates") {
            return;
        }
        getAllTemplates();
    }, [getAllTemplates, view]);

    const templatesToNoShow = [
        "authorize_failure_patient_template",
        "authorize_success_patient_template",
        "authorize_success_provider_template",
        "charge_success_patient_template",
        "contact_form_template",
        "appointment_update_template",
    ];

    const filteredResult = templateResponse?.templates?.result?.filter(
        (item: any) => !templatesToNoShow?.includes(item.name)
    );

    const consentTemplateNames = [
        "parkhill_consent_form",
        "humantold_consent_form",
        "chp_testing_consent_form",
        "kap_consent",
        "chp_psychiatry_consent_form",
        "chp_nutrition_counseling_consent_form",
        "chp_therapy_consent_form",
        "chp_career_assessment_consent_form",
    ];

    const filteredResultWithConsents = React.useMemo(() => {
        const base = filteredResult || [];
        const existingNames = new Set(base.map((t: any) => t.name));

        const extraTemplates = consentTemplateNames
            .filter((name) => !existingNames.has(name))
            .map((name) => ({
                id: name,
                name,
                generation: "",
                updated_at: new Date().toISOString(),
                versions: [{ subject: "" }],
            }));

        return [...base, ...extraTemplates];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredResult?.length]);

    // Update count when data changes
    React.useEffect(() => {
        if (onCountChange) {
            if (view === "templates") {
                onCountChange(filteredResultWithConsents?.length || 0);
            } else {
                onCountChange(outboundEmails.length);
            }
        }
    }, [
        onCountChange,
        view,
        filteredResultWithConsents?.length,
        outboundEmails.length,
    ]);

    const columns = React.useMemo(() => {
        return [
            {
                Header: "Date",
                id: "queued-at",
                accessor: (row: INewOutboundEmail) => (
                    <p>
                        {`${moment((row.created_at as any).toDate()).format(
                            DateFormats.DAY_HOUR_MIN_FORMAT
                        )}`}
                    </p>
                ),
            },
            {
                Header: "Recipient Type",
                id: "email-to",
                accessor: (row: INewOutboundEmail) => (
                    <span className={styles.recipientType}>
                        {removeEnumUnderscore(row.recipient_type)}
                    </span>
                ),
            },
            {
                Header: "Recipient Email",
                accessor: "recipient.email",
            },
            {
                Header: "First Name",
                accessor: (row: INewOutboundEmail) =>
                    row.recipient.first_name ? row.recipient.first_name : "--",
            },
            {
                Header: "Subject",
                accessor: (row: INewOutboundEmail) => (
                    <p className={styles.emailText}>{row.subject}</p>
                ),
            },
            {
                Header: "Preview Text",
                accessor: (row: INewOutboundEmail) => (
                    <p className={styles.emailText}>
                        {row.preview_text
                            ? row.preview_text
                            : "No preview available"}
                    </p>
                ),
            },
            {
                Header: "Status",
                id: "status-tag",
                accessor: (row: INewOutboundEmail) => (
                    <EmailStatusTag status={row.status} />
                ),
            },
            {
                Header: "Actions",
                id: "actions",
                accessor: (row: INewOutboundEmail) => {
                    const canRetry =
                        row.status === OutboundEmailStatus.PENDING ||
                        row.status === OutboundEmailStatus.FAILED ||
                        row.status === OutboundEmailStatus.UNSCHEDULED;

                    const handleRetry = async () => {
                        try {
                            const retriedEmailId = await retryOutboundEmail(
                                row.id as string
                            );
                            addToast(
                                retriedEmailId
                                    ? "Email queued for retry"
                                    : "Email was already queued for retry",
                                {
                                    appearance: "success",
                                    autoDismiss: false,
                                }
                            );
                            fetchSentOutboundEmails();
                        } catch (e) {
                            console.error(e);
                        }
                    };

                    return canRetry ? (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={(props) => (
                                <Tooltip id="button-tooltip-cancel" {...props}>
                                    Retry email
                                </Tooltip>
                            )}
                        >
                            <button
                                className="unstyled-button"
                                onClick={handleRetry}
                            >
                                <FontAwesomeIcon
                                    className="hover-effect"
                                    icon={faRedo}
                                />
                            </button>
                        </OverlayTrigger>
                    ) : (
                        <span />
                    );
                },
            },
        ];
    }, [addToast, fetchSentOutboundEmails]);

    return (
        <div>
            {view === "sent-emails" ? (
                <>
                    <div className={styles.filter_container}>
                        <SearchInput
                            aria-label="Search emails on this page"
                            containerClass={styles.emailSearch}
                            placeholder="Search by first name, email, subject, or preview"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                        <div className={styles.filterField}>
                            <p>Email type:</p>
                            <Dropdown>
                                <Dropdown.Trigger>
                                    <FilterButton
                                        classNames={styles.filterButton}
                                        text={
                                            <span
                                                className={styles.filterButtonText}
                                            >
                                                {filterByType === ALL_EMAIL_TYPES
                                                    ? "All"
                                                    : removeEnumUnderscore(
                                                          OutboundEmailTypeDescription[
                                                              Object.keys(
                                                                  OutboundEmailType
                                                              ).find(
                                                                  (key) =>
                                                                      OutboundEmailType[
                                                                          key as keyof typeof OutboundEmailType
                                                                      ] ===
                                                                      filterByType
                                                              ) as keyof typeof OutboundEmailTypeDescription
                                                          ]
                                                      )}
                                            </span>
                                        }
                                    />
                                </Dropdown.Trigger>
                                <Dropdown.Content className={styles.filter_dropdown}>
                                    <Dropdown.Item
                                        key="type-all"
                                        closeMenuOnSelect
                                        isSelected={filterByType === ALL_EMAIL_TYPES}
                                        onClick={() => {
                                            setFilterByType(ALL_EMAIL_TYPES);
                                        }}
                                    >
                                        All Email Types
                                    </Dropdown.Item>
                                    {Object.keys(OutboundEmailTypeDescription).map(
                                        (s) => (
                                            <Dropdown.Item
                                                key={`type-${s}`}
                                                closeMenuOnSelect
                                                isSelected={
                                                    s ===
                                                    Object.keys(
                                                        OutboundEmailTypeDescription
                                                    ).find(
                                                        (key) =>
                                                            OutboundEmailType[
                                                                key as keyof typeof OutboundEmailType
                                                            ] === filterByType
                                                    )
                                                }
                                                onClick={() => {
                                                    setFilterByType(
                                                        OutboundEmailType[
                                                            s as OutboundEmailType
                                                        ]
                                                    );
                                                }}
                                            >
                                                {removeEnumUnderscore(
                                                    OutboundEmailTypeDescription[s]
                                                )}
                                            </Dropdown.Item>
                                        )
                                    )}
                                </Dropdown.Content>
                            </Dropdown>
                        </div>
                        <div className={styles.filterField}>
                            <p>Status:</p>
                            <Dropdown>
                                <Dropdown.Trigger>
                                    <FilterButton
                                        classNames={styles.filterButton}
                                        text={
                                            <span
                                                className={styles.filterButtonText}
                                            >
                                                {filterByStatus ===
                                                ALL_EMAIL_STATUSES
                                                    ? "All"
                                                    : OutboundEmailStatusDescription[
                                                          filterByStatus
                                                      ]}
                                            </span>
                                        }
                                    />
                                </Dropdown.Trigger>
                                <Dropdown.Content className={styles.filter_dropdown}>
                                    <Dropdown.Item
                                        key="status-all"
                                        closeMenuOnSelect
                                        isSelected={
                                            filterByStatus === ALL_EMAIL_STATUSES
                                        }
                                        onClick={() => {
                                            setFilterByStatus(ALL_EMAIL_STATUSES);
                                        }}
                                    >
                                        All Statuses
                                    </Dropdown.Item>
                                    {Object.values(OutboundEmailStatus).map(
                                        (status) => (
                                            <Dropdown.Item
                                                key={`status-${status}`}
                                                closeMenuOnSelect
                                                isSelected={
                                                    filterByStatus === status
                                                }
                                                onClick={() => {
                                                    setFilterByStatus(status);
                                                }}
                                            >
                                                {
                                                    OutboundEmailStatusDescription[
                                                        status
                                                    ]
                                                }
                                            </Dropdown.Item>
                                        )
                                    )}
                                </Dropdown.Content>
                            </Dropdown>
                        </div>
                        <div className={styles.filterField}>
                            <p>Recipient:</p>
                            <Dropdown>
                                <Dropdown.Trigger>
                                    <FilterButton
                                        classNames={styles.filterButton}
                                        text={
                                            <span
                                                className={styles.filterButtonText}
                                            >
                                                {filterByRecipientType ===
                                                ALL_RECIPIENT_TYPES
                                                    ? "All"
                                                    : EmailRecipientTypeDescription[
                                                          filterByRecipientType
                                                      ]}
                                            </span>
                                        }
                                    />
                                </Dropdown.Trigger>
                                <Dropdown.Content className={styles.filter_dropdown}>
                                    <Dropdown.Item
                                        key="recipient-all"
                                        closeMenuOnSelect
                                        isSelected={
                                            filterByRecipientType ===
                                            ALL_RECIPIENT_TYPES
                                        }
                                        onClick={() =>
                                            setFilterByRecipientType(
                                                ALL_RECIPIENT_TYPES
                                            )
                                        }
                                    >
                                        All Recipient Types
                                    </Dropdown.Item>
                                    {Object.entries(
                                        EmailRecipientTypeDescription
                                    ).map(([recipientType, description]) => (
                                        <Dropdown.Item
                                            key={`recipient-${recipientType}`}
                                            closeMenuOnSelect
                                            isSelected={
                                                filterByRecipientType ===
                                                recipientType
                                            }
                                            onClick={() =>
                                                setFilterByRecipientType(
                                                    recipientType as EmailRecipientType
                                                )
                                            }
                                        >
                                            {description}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Content>
                            </Dropdown>
                        </div>

                        <Button
                            className={styles.refreshButton}
                            onClick={() => {
                                fetchSentOutboundEmails();
                            }}
                        >
                            <RefreshIcon />
                        </Button>
                    </div>
                    {loading ? (
                        <Skeleton count={8} />
                    ) : outboundEmails && outboundEmails.length > 0 ? (
                        <>
                            {visibleOutboundEmails.length > 0 ? (
                                <Table
                                    stickyLastColumn
                                    columns={columns}
                                    allowSelect={false}
                                    data={visibleOutboundEmails}
                                />
                            ) : (
                                <EmptyListState
                                    emptyMessage="No matching emails on this page"
                                    stateHelperText="Try another search or page"
                                />
                            )}
                            <TablePagination
                                currentPage={currentPage}
                                onFirstPage={handleFirstPageClick}
                                onPrevPage={handlePrevClick}
                                onNextPage={handleNextClick}
                                isNextDisabled={nextButtonDisabled}
                                showPrevButton={currentPage > 1}
                                pageNumberText={`Page ${currentPage}`}
                            />
                        </>
                    ) : (
                        <EmptyListState
                            emptyMessage="No emails found"
                            stateHelperText="Emails will be found here when sent"
                        />
                    )}
                </>
            ) : (
                <TemplateList
                    templates={filteredResultWithConsents}
                    onRefresh={getAllTemplates}
                    client={client}
                />
            )}
        </div>
    );
}
